import React, { Component } from 'react';
import t from 'prop-types';
import { I18n } from '@kineticdata/react';
import moment from 'moment';

const pad = value => (`${value}`.length === 1 ? `0${value}` : value);

export class Countdown extends Component {
  constructor(props) {
    super(props);
    this.state = {
      target: null,
      invalid: false,
      days: null,
      hours: null,
      minutes: null,
      seconds: null,
      isCompleted: false,
      display: [
        Countdown.DAYS,
        Countdown.HOURS,
        Countdown.MINUTES,
        Countdown.SECONDS,
      ].includes(props.display)
        ? props.display
        : Countdown.HOURS,
      direction: [
        Countdown.DIR_DOWN,
        Countdown.DIR_UP,
        Countdown.DIR_BOTH,
      ].includes(props.direction)
        ? props.direction
        : Countdown.DIR_DOWN,
      pending: false,
    };
    this.intervalId = null;
  }

  calc = target => {
    const duration = moment.duration(
      (target || this.state.target).diff(moment()),
    );
    if (
      this.state.direction === Countdown.DIR_BOTH ||
      (this.state.direction === Countdown.DIR_DOWN &&
        duration.asMilliseconds() > 0) ||
      (this.state.direction === Countdown.DIR_UP &&
        duration.asMilliseconds() < 0)
    ) {
      return {
        days:
          this.state.display === Countdown.DAYS
            ? Math.floor(Math.abs(duration.asDays()))
            : 0,
        hours:
          this.state.display === Countdown.HOURS
            ? Math.floor(Math.abs(duration.asHours()))
            : this.state.display === Countdown.DAYS
              ? Math.abs(duration.hours())
              : 0,
        minutes:
          this.state.display === Countdown.MINUTES
            ? Math.floor(Math.abs(duration.asMinutes()))
            : this.state.display !== Countdown.SECONDS
              ? Math.abs(duration.minutes())
              : 0,
        seconds:
          this.state.display === Countdown.SECONDS
            ? Math.floor(Math.abs(duration.asSeconds()))
            : Math.abs(duration.seconds()),
        isCompleted: duration.asMilliseconds() <= 0,
      };
    } else {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        isCompleted: duration.asMilliseconds() <= 0,
      };
    }
  };

  tick = () => this.setState(this.calc());

  calcInitState() {
    const target = moment(this.props.date);
    if (this.props.date && target.isValid()) {
      return { target, ...this.calc(target) };
    } else {
      return { invalid: true, target: null };
    }
  }

  componentDidMount() {
    this.setState(this.calcInitState());
  }

  componentDidUpdate(prevProps, prevState) {
    // If date or display changes, reset state
    if (
      this.props.date !== prevProps.date ||
      this.props.display !== prevProps.display
    ) {
      this.setState(this.calcInitState());
    }

    if (this.state.target && !prevState.target && !this.props.static) {
      this.intervalId = window.setInterval(this.tick, 1000);
    }

    if (this.state.isCompleted && !prevState.isCompleted) {
      if (this.state.direction === Countdown.DIR_DOWN) {
        this.intervalId = window.clearInterval(this.intervalId);
      }
      if (!!prevState.target && typeof this.props.onComplete === 'function') {
        this.props.onComplete();
      }
    }
  }

  componentWillUnmount() {
    window.clearInterval(this.intervalId);
  }

  render() {
    const { children: Wrapper } = this.props;
    const days =
      this.state.display === Countdown.DAYS ? this.state.days : undefined;
    const hours = [Countdown.DAYS, Countdown.HOURS].includes(this.state.display)
      ? this.state.hours
      : undefined;
    const minutes =
      this.state.display !== Countdown.SECONDS ? this.state.minutes : undefined;
    const seconds = this.state.seconds;
    const content = this.state.invalid ? (
      <I18n>Invalid Date</I18n>
    ) : this.state.target && this.state.seconds !== null ? (
      `${days !== undefined ? `${days}d ` : ''}${
        hours !== undefined ? `${pad(hours)}:` : ''
      }${minutes !== undefined ? `${pad(minutes)}:` : ''}${
        this.state.display === Countdown.SECONDS ? seconds : pad(seconds)
      }`
    ) : null;
    return Wrapper ? (
      <Wrapper
        content={content}
        invalid={this.state.invalid}
        isCompleted={this.state.isCompleted}
        days={days}
        hours={hours}
        minutes={minutes}
        seconds={seconds}
      />
    ) : (
      content
    );
  }

  static DAYS = 'd';
  static HOURS = 'h';
  static MINUTES = 'm';
  static SECONDS = 's';

  static DIR_DOWN = 'down';
  static DIR_UP = 'up';
  static DIR_BOTH = 'both';
}

Countdown.propTypes = {
  /** Date object or date string showing time to which we are counting down. */
  date: t.oneOfType([t.object, t.string]),
  /** The direction of the countdown. Valid options: [down, up, both] */
  direction: t.string,
  /** Callback function to be called when the countdown reaches 0. */
  onComplete: t.func,
  /** Format in which countdown should be displayed. Valid options: [d, h, m, s] */
  display: t.string,
  /** Flag to define if the countdown shouldn't tick. Defaults to false. */
  static: t.bool,
  /** Render function to wrap the content returned. Receives props: content, isCompleted, hours, minutes, seconds */
  children: t.func,
};
